<template>
  <vx-card class="mt-base" :title="activityTitle">
    <img
      src="@/assets/images/experiences/gamesdeveloper/activity_1_side_image.jpg"
      alt="A game studio where artists work on character concept art, with sketches pinned to the wall and on screen."
      class="my-2 ml-2 mb-2 float-right"
    />

    <p class="mb-5">In this activity, you will develop your game ideas and complete 2 tasks.</p>
    <p class="mb-5">
      While there are many differences between games and fictional books or movies, there are many
      similarities. Not the least of these is that you need a kernel of a story idea to get things
      started with each.
    </p>
    <p class="mb-5">
      Every game begins with a concept. Very often, this initial idea comes from a story you’d love
      to tell, a game-playing experience you want to share, or a new technology you’d like to
      incorporate.
    </p>
    <p class="mb-5">
      While reading a story or watching a movie is passive entertainment, games are interactive,
      with players usually taking on the role of a character that interacts and affects the
      fictional world around them.
    </p>
    <p class="mb-5">
      The game development process often starts by brainstorming to come up with an idea for a game.
      This will usually involve some sort of story line (no matter how thin!) and some goals that
      the players will strive to achieve. Good questions to start the process include:
    </p>

    <ul class="default-list">
      <li>What will the player do?</li>
      <li>Why will they do it?</li>
      <li>How will they do it?</li>
    </ul>
    <p class="mb-5">
      While story isn’t everything in a game, every game should have at least some story-ness to it.
      Some of the familiar story elements you might find in games you love include:
    </p>

    <ul class="default-list">
      <li>Intro: Setting, Characters, Conflict</li>
      <li>Rising Action</li>
      <li>Climax</li>
      <li>Falling Action</li>
      <li>Resolution</li>
    </ul>

    <p class="mb-5">
      Much like other creative jobs, one of the most difficult aspects of game development is coming
      up with that initial idea to get the ball rolling.
    </p>
    <p class="mb-5">In this task, your job is to come up with 3 basic ideas for video games.</p>
    <p class="mb-5">
      Now, there’s no magic formula to follow, but there are some places you can look for
      inspiration.
    </p>
    <p class="mb-5">Let’s take a look at some of these:</p>

    <ul class="default-list">
      <li>
        Other Video Games: While you certainly shouldn’t copy another game, you can look to other
        games out there for inspiration. Maybe there are some games you enjoy, but you’d like to see
        them done from another perspective or with some specific improvements made. Just be sure to
        bring some originality to what you do if you decide to go this route.
      </li>
      <li>
        Real-Life Games: It isn’t only video games that can inspire you; you can also find ideas in
        more traditional types of games, puzzles, and sports. What are your favourite real-life
        games?
      </li>
      <li>
        Movies: Watching lots of movies can provide some powerful fuel for your creative engine. Who
        knew ‘research’ could be such fun! Movie-Game tie-ins are very common these days, but
        tie-ins aren’t the only options. A movie may inspire you, but you may still go off on a
        completely original tangent too.
      </li>
      <li>
        Dreams: For millennia, dreams (and nightmares) have been a rich vein tapped by storytellers.
        They can be a generous source of inspiration for the observant games developer too.
      </li>
    </ul>
    <p class="mb-5">
      Watch the following video to learn more about some activities you can do to help you generate
      ideas for your video game.
    </p>

    <responsive-youtube-wrapper :video="videos[1].video" :image="videos[1].image" />

    <h4 class="mb-5">Task 1: Your Best 3 Ideas:</h4>

    <p class="mb-5">
      Once you’ve worked through some of the brainstorming activities above and come up with a few
      ideas, record a brief description of your 3 best ideas in the spaces below:
    </p>

    <div v-for="index in 3" :key="'text_' + index">
      <p class="mb-3">Idea {{ index }}:</p>
      <textarea-block v-model="data['question' + index]" :editable="isEditable" />
    </div>

    <p class="mb-5">
      If your brainstorming sessions produced sketches, notes, images, etc., you can upload pictures
      of these here.
    </p>
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_' + index]"
      :is-editable="isEditable"
      :index="index"
      filetype="media"
      v-for="index in 3"
      :key="'file_' + index"
    />

    <h4 class="my-5">Task 2: Your Chosen Idea to Move Forward With:</h4>
    <p class="mb-5">
      Now that you have a few game ideas, you’ll need to choose one to continue the planning
      process. Choose one that has people as characters (i.e., rather than animals or machines).
    </p>
    <p class="mb-5">Record a working title for your main idea here:</p>

    <text-block v-model="data['question4']" :editable="isEditable" />
    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/gamesdeveloper/how_to_come_up_with_game_ideas_splash.jpg'),
          video: 'https://www.youtube.com/embed/RRu6mlaSdT0',
        },
      },
      image1: require('@/assets/images/experiences/gamesdeveloper/activity_1_1.png'),
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
        uploaded_file_3: '',
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.uploaded_files !== 'undefined') {
      for (let i = 1; i <= Object.keys(this.uploaded_files).length; i++) {
        if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
          this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
        }
      }
    }
  },
};
</script>
